// assets/tryon-reinject.js
(() => {
  /**
   * Re-inject Tryon <tryon-button> if Prestige (or similar) re-renders product DOM
   * and removes the injected element (e.g., on swatch/variant change).
   *
   * Theme-side only: does not require changes to the app script.
   */

  const SELECTORS = {
    productRerender: 'product-rerender[id^="product-info-"]',
    tryonButton: "tryon-button",
    // ATC fallbacks (aligned with your app script's fallbacks)
    atc: [
      'product-form form button[type="submit"]',
      'form[action$="/cart/add"] button[type="submit"]',
      "#AddToCart",
      "button.add-to-cart__button",
    ],
  };

  // Cached "template" of the first Tryon button we see (full markup).
  let cachedTryonHTML = null;

  // Observers & scheduling
  let productObserver = null;
  let bodyObserver = null;
  let scheduled = false;

  function findAtcButton(root = document) {
    for (const sel of SELECTORS.atc) {
      const el = root.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function captureTryonTemplate() {
    if (cachedTryonHTML) return true;

    const btn = document.querySelector(SELECTORS.tryonButton);
    if (!btn) return false;

    // Cache the exact markup that worked initially.
    cachedTryonHTML = btn.outerHTML;
    return true;
  }

  function reinjectIfMissing() {
    // If it exists, nothing to do.
    if (document.querySelector(SELECTORS.tryonButton)) return;

    // If we don't yet have a template, try to capture it (might appear shortly after load)
    if (!cachedTryonHTML && !captureTryonTemplate()) return;

    const atc = findAtcButton();
    if (!atc || !atc.parentNode) return;

    // Insert the cached HTML after the ATC button.
    // When inserted, the browser upgrades <tryon-button> and the app constructor runs again.
    atc.insertAdjacentHTML("afterend", cachedTryonHTML);
  }

  function scheduleWork() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      // Try to capture (if first appearance happens now) and ensure button exists.
      captureTryonTemplate();
      reinjectIfMissing();
    });
  }

  // Short polling as a fallback to capture the first Tryon button if it's injected later.
  function waitForTryonCapture(maxMs = 8000, intervalMs = 200) {
    const start = Date.now();
    const t = setInterval(() => {
      if (cachedTryonHTML) {
        clearInterval(t);
        return;
      }
      captureTryonTemplate();
      if (cachedTryonHTML || Date.now() - start > maxMs) {
        clearInterval(t);
      }
    }, intervalMs);
  }

  // Observe the product rerender container (Prestige swaps DOM inside it on swatch changes)
  function attachProductObserver() {
    const pr = document.querySelector(SELECTORS.productRerender);
    if (!pr) return;

    // Initial attempt
    scheduleWork();

    if (productObserver) productObserver.disconnect();
    productObserver = new MutationObserver(() => scheduleWork());
    productObserver.observe(pr, { childList: true, subtree: true });
  }

  // Observe body until the first Tryon button appears (then disconnect).
  // This helps if the app injects outside product-rerender or if product-rerender isn't present yet.
  function attachBodyObserverUntilCaptured() {
    if (cachedTryonHTML) return;

    if (bodyObserver) bodyObserver.disconnect();
    bodyObserver = new MutationObserver(() => {
      if (captureTryonTemplate()) {
        // Template captured; no need to keep watching the whole body.
        bodyObserver.disconnect();
        bodyObserver = null;
      }
      // Either way, ensure button exists if it was removed.
      scheduleWork();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  function attachAll() {
    attachProductObserver();
    attachBodyObserverUntilCaptured();
    waitForTryonCapture(); // polling fallback (stops automatically)
  }

  // Run on initial page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachAll);
  } else {
    attachAll();
  }

  // Shopify section reloads (theme editor, some dynamic renders)
  document.addEventListener("shopify:section:load", attachAll);

  // Extra fallback: variant changes sometimes happen without big DOM mutations
  document.addEventListener(
    "change",
    (e) => {
      if (
        e.target &&
        e.target.closest(
          'form[action$="/cart/add"], product-form, variant-radios, variant-selects, .product-form',
        )
      ) {
        scheduleWork();
      }
    },
    true,
  );
})();